import { useMemo } from 'react';
import type { InitiativeDetailResponse, MasterDataSet } from '@complan/contracts';
import {
  toNameLookup,
  useSelectableMasterDataSets,
  type SelectableRecord,
} from '@/shared/api/selectable-master-data';
import { useSalesCycleLabels } from './list/initiative-list-data';

/**
 * Display names for the master-data identifiers the Detail_Panel renders.
 *
 * `GET /api/initiatives/:id` returns association *ids*, not names (see
 * `initiativeDetailResponseSchema`) - deliberately, since the names are master
 * data the client already caches and repeating them per record would put the same
 * string on the wire once per association. The panel therefore resolves them, and
 * this hook is the one place that does it, so the Overview, Workload, Milestones
 * and Dependencies tabs cannot disagree about what a given id is called.
 *
 * ## Deactivated values still resolve
 *
 * Every id the record actually references is passed as `includeIds`, so a value
 * deactivated after it was assigned still comes back (flagged inactive) and still
 * renders as a name. Without that, an old Initiative's classification would
 * degrade to raw UUIDs precisely when a reader most needs to understand it -
 * the same reasoning R11.2/R11.15 apply to the calendar's filters, and R5.9 to
 * the intake form's controlled values.
 *
 * Sales_Cycle names come from `useSalesCycleLabels` (all years, inactive
 * included) rather than the year-scoped calendar read: an Initiative's date range
 * may intersect more than one planning year (R4.12), so a single year's
 * definitions would leave some assigned cycles unresolved.
 */

/** The master-data sets the Detail_Panel needs names from. */
const DETAIL_SETS: readonly MasterDataSet[] = [
  'platform',
  'channel',
  'customer-group',
  'audience',
  'category',
  'initiative-type',
  'priority',
  'ff-activity-type',
  'region',
  'population-unit',
  'pos-segment',
  'csc-attribute',
];

export interface DetailReferenceData {
  /** One id's display name, falling back to the id so a cell is never blank. */
  readonly nameOf: (set: MasterDataSet, id: string) => string;
  /** Several ids of one set joined for display, or `emptyText` when there are none. */
  readonly namesOf: (set: MasterDataSet, ids: readonly string[], emptyText: string) => string;
  /** One Sales_Cycle's name, falling back to the id. */
  readonly salesCycleName: (id: string) => string;
  /** Several Sales_Cycle names joined, or `emptyText` when there are none. */
  readonly salesCycleNames: (ids: readonly string[], emptyText: string) => string;
  /** The full record for a set, when a caller needs more than the name (POS counts). */
  readonly recordsOf: (set: MasterDataSet) => readonly SelectableRecord[];
  readonly isLoading: boolean;
}

/**
 * Resolves every master-data name the panel shows for one Initiative.
 *
 * `detail` is read only for the ids it references, which become the `includeIds`
 * of each set - see this module's note on deactivated values.
 */
export function useDetailReferenceData(
  detail: InitiativeDetailResponse | undefined,
): DetailReferenceData {
  const includeIdsBySet = useMemo((): Partial<Record<MasterDataSet, readonly string[]>> => {
    if (detail === undefined) return {};
    const scope = detail.fieldForceScope;
    return {
      platform: detail.platforms.map((link) => link.platformId),
      channel: [...detail.channelIds],
      'customer-group': [...detail.customerGroupIds],
      audience: [
        ...detail.audienceIds,
        // A scope entry's audience may differ from the Initiative's own set.
        ...scope.flatMap((entry) => (entry.audienceId === null ? [] : [entry.audienceId])),
      ],
      category: [...detail.categoryIds],
      'initiative-type': [...detail.initiativeTypeIds],
      priority: [detail.priorityId],
      'ff-activity-type': scope.map((entry) => entry.activityTypeId),
      region: scope.flatMap((entry) => (entry.regionId === null ? [] : [entry.regionId])),
      'population-unit': scope.flatMap((entry) =>
        entry.populationUnitId === null ? [] : [entry.populationUnitId],
      ),
      'pos-segment': scope.flatMap((entry) => [...entry.posSegmentIds]),
      'csc-attribute': [...detail.cscAttributeIds],
    };
  }, [detail]);

  const sets = useSelectableMasterDataSets(DETAIL_SETS, {
    includeIdsBySet,
    enabled: detail !== undefined,
  });
  const salesCycles = useSalesCycleLabels();

  const lookupBySet = useMemo(() => {
    const map = new Map<MasterDataSet, ReadonlyMap<string, string>>();
    for (const set of DETAIL_SETS) {
      map.set(set, toNameLookup(sets.bySet.get(set)));
    }
    return map;
  }, [sets.bySet]);

  const salesCycleLookup = useMemo(
    () => new Map((salesCycles.data ?? []).map((cycle) => [cycle.id, cycle.label] as const)),
    [salesCycles.data],
  );

  return useMemo(() => {
    const nameOf = (set: MasterDataSet, id: string): string =>
      lookupBySet.get(set)?.get(id) ?? id;
    const namesOf = (set: MasterDataSet, ids: readonly string[], emptyText: string): string =>
      ids.length === 0 ? emptyText : ids.map((id) => nameOf(set, id)).join(', ');
    const salesCycleName = (id: string): string => salesCycleLookup.get(id) ?? id;
    return {
      nameOf,
      namesOf,
      salesCycleName,
      salesCycleNames: (ids, emptyText) =>
        ids.length === 0 ? emptyText : ids.map(salesCycleName).join(', '),
      recordsOf: (set) => sets.bySet.get(set) ?? [],
      isLoading: sets.isLoading || salesCycles.isLoading,
    };
  }, [lookupBySet, salesCycleLookup, sets, salesCycles.isLoading]);
}
